import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import { useNavigation } from '@react-navigation/native';

import { CheckmarkLargeWhite } from '@/assets/icons/checkmark-large-white';
import { Button } from '@/components/ButtonComponent';
import { Screen } from '@/components/ScreenComponent';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { handleUserBlockedError } from '@/feature/authentication/utils';
import { KycProvider } from '@/feature/kyc/const';
import { useNavigateToDeposit } from '@/feature/kyc/hooks/use-navigate-to-deposit';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useAuthUserConfig } from '@/hooks/use-auth-user-config';
import { useAvailableProducts } from '@/hooks/use-available-products';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { usePreventAndroidBackNavigation } from '@/hooks/use-prevent-android-back-navigation';
import { usePrefetchPromoCodeDefaultProductSyncMap } from '@/hooks/use-promo-code-default-product-sync';
import { useRootNavigation } from '@/hooks/use-root-navigation';
import { KycStackNavigationProp } from '@/navigation/kyc/types';
import { triggerHapticFeedback } from '@/utils/haptic-feedback';

export const VerificationSuccess = (): React.ReactElement | null => {
    const activeProduct = useJurisdictionStore(state => state.product);
    const { t } = useTranslation(['kyc', 'common']);
    const navigation = useNavigation<KycStackNavigationProp<'KycVerificationSuccess'>>();
    const rootNavigation = useRootNavigation();

    const { error, isFetched } = useAuthUserConfig();
    const isUserBlockedError = isFetched && error;

    usePreventAndroidBackNavigation();

    usePrefetchPromoCodeDefaultProductSyncMap();

    const navigateToDeposit = useNavigateToDeposit();
    const { availableProducts } = useAvailableProducts();
    const shouldSelectDefaultProduct = availableProducts.filter(product => product !== Product.None).length > 1;
    const [hasNextButton, setHasNextButton] = useState(false);

    const goToDefaultProductSelection = useCallback(() => {
        navigation.navigate('DefaultProductOnboarding', { kycProvider: KycProvider.IDCOMPLY });
    }, [navigation]);

    const proceedToDeposit = useCallback(
        (product: Product) => {
            if (shouldSelectDefaultProduct) {
                goToDefaultProductSelection();
            } else {
                navigateToDeposit(product);
            }
        },
        [shouldSelectDefaultProduct, goToDefaultProductSelection, navigateToDeposit]
    );

    useEffect(() => {
        if (!isUserBlockedError) {
            triggerHapticFeedback();

            // Navigate to DepositScreen after 2 seconds
            const timeout = setTimeout(() => proceedToDeposit(activeProduct), 1000);

            // Show the next button after 3 seconds incase automatic navigation fails
            const nextTimeout = setTimeout(() => {
                setHasNextButton(true);
            }, 3000);

            return () => {
                clearTimeout(timeout);
                clearTimeout(nextTimeout);
            };
        }
    }, [activeProduct, proceedToDeposit, isUserBlockedError]);

    useEffect(() => {
        if (isUserBlockedError) {
            return handleUserBlockedError(error, rootNavigation);
        }
    }, [error, isUserBlockedError, rootNavigation]);

    if (isUserBlockedError) {
        return null;
    }

    return (
        <Screen>
            <MaxWidthWrapper flex={1} paddingHorizontal={'s16'}>
                <SizedBox value={72} />
                <Box flex={1} alignItems="center" justifyContent="center" paddingHorizontal="s16">
                    <CheckmarkLargeWhite />
                    <SizedBox value={16} />
                    <Text variant="titleSmall">{t('common:identity_verified')}</Text>
                </Box>
                <Box position="relative" padding="s16">
                    <Button
                        label={t('common:next')}
                        hierarchy="primary"
                        style={!hasNextButton && styles.hidden}
                        onPress={() => proceedToDeposit(activeProduct)}
                    />
                </Box>
            </MaxWidthWrapper>
        </Screen>
    );
};

const styles = StyleSheet.create({
    hidden: { opacity: 0 },
});
